/**
 * RelatedNews — seção "Veja Também" da view de Notícia.
 *
 * Busca notícias relacionadas (mesmo(s) Subject, quando fornecidos) via
 * redux do Volto, excluindo a notícia atual do resultado.
 *
 * Os Subjects seguem escolhendo QUAIS notícias aparecem, mas não são exibidos
 * como chip no card — as tags saíram da interface em 2026-09-08.
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import Component from '@plone/volto/components/theme/Component/Component';
import FormattedDate from '@plone/volto/components/theme/FormattedDate/FormattedDate';
import { searchContent } from '@plone/volto/actions/search/search';

const messages = defineMessages({
  heading: {
    id: 'relatedNews.heading',
    defaultMessage: 'See also',
  },
  loading: {
    id: 'relatedNews.loading',
    defaultMessage: 'Loading…',
  },
  previous: {
    id: 'pagination.previous',
    defaultMessage: 'Previous page',
  },
  next: {
    id: 'pagination.next',
    defaultMessage: 'Next page',
  },
});

const SUBREQUEST_KEY = 'related-news';
const PAGE_SIZE = 4;

// Ver nota em NewsCenterTemplate: sem `sizes` o PreviewImage baixa a escala do
// tamanho da janela. Grade de RelatedNews.scss: 4 col (lg) / 2 col (md) / 1.
const CARD_SIZES = '(min-width: 941px) 280px, (min-width: 769px) 45vw, 100vw';

type RelatedItem = {
  '@id': string;
  title?: string;
  id?: string;
  effective?: string;
  Subject?: string[];
  [key: string]: unknown;
};

type SearchSubrequestState = {
  items: RelatedItem[];
  total: number;
  loading: boolean;
};

export type RelatedNewsProps = {
  currentPath: string;
  subjects?: string[];
  title?: string;
};

const RelatedNews = ({ currentPath, subjects, title }: RelatedNewsProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);

  const subrequest = useSelector(
    (state: {
      search: { subrequests: Record<string, SearchSubrequestState> };
    }) => state.search.subrequests[SUBREQUEST_KEY],
  );

  const items = subrequest?.items ?? [];
  const total = subrequest?.total ?? 0;
  const loading = subrequest?.loading ?? false;

  const subjectsKey = subjects?.join(',') ?? '';

  useEffect(() => {
    const query: Record<string, unknown> = {
      portal_type: ['News Item'],
      sort_on: 'effective',
      sort_order: 'descending',
      b_size: PAGE_SIZE,
      b_start: page * PAGE_SIZE,
    };
    if (subjects && subjects.length > 0) {
      query.Subject = subjects;
    }
    dispatch(searchContent('', query, SUBREQUEST_KEY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page, subjectsKey]);

  const filteredItems = items.filter(
    (item) => !item['@id']?.endsWith(currentPath),
  );

  if (loading) {
    return (
      <p className="related-news__loading">
        {intl.formatMessage(messages.loading)}
      </p>
    );
  }

  if (filteredItems.length === 0) {
    return null;
  }

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const showPagination = total > PAGE_SIZE;

  return (
    <section className="related-news">
      <h2>{title || intl.formatMessage(messages.heading)}</h2>
      <ul className="related-news__grid">
        {filteredItems.map((item) => {
          return (
            <li className="related-news__card" key={item['@id']}>
              <ConditionalLink item={item} condition>
                <Component
                  componentName="PreviewImage"
                  item={item}
                  alt=""
                  loading="lazy"
                  sizes={CARD_SIZES}
                />
                <h3 className="related-news__title">{item.title || item.id}</h3>
                {item.effective && (
                  <span className="related-news__date">
                    <FormattedDate date={item.effective} />
                  </span>
                )}
              </ConditionalLink>
            </li>
          );
        })}
      </ul>
      {showPagination && (
        <div className="related-news__pagination">
          <button
            type="button"
            className="related-news__pagination-button related-news__pagination-button--prev"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            aria-label={intl.formatMessage(messages.previous)}
          >
            {intl.formatMessage(messages.previous)}
          </button>
          <button
            type="button"
            className="related-news__pagination-button related-news__pagination-button--next"
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            disabled={page >= lastPage}
            aria-label={intl.formatMessage(messages.next)}
          >
            {intl.formatMessage(messages.next)}
          </button>
        </div>
      )}
    </section>
  );
};

export default RelatedNews;
